import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { AppShell, Sidebar, TeamPage, TopBar, type TeamMemberRow } from '@suvoda-uat/ui-suvoda';
import { ApiError, type TeamMember } from '../lib/api-client.js';
import { canManageTeam, currentUserName, signOut } from '../lib/auth.js';
import { navTargetFor, visibleNavItems } from '../lib/nav.js';
import { useTeamMembers } from '../lib/queries.js';

export const Route = createFileRoute('/team')({
  // Graceful FE guard (roles decode synchronously from the stored ID token);
  // the API's admin-tier 403 stays authoritative regardless.
  beforeLoad: () => {
    if (!canManageTeam()) throw redirect({ to: '/' });
  },
  component: TeamRoute,
});

const toRow = (member: TeamMember): TeamMemberRow => ({
  id: member.id,
  ...(member.name === undefined ? {} : { name: member.name }),
  ...(member.email === undefined ? {} : { email: member.email }),
  roles: [...member.roles],
  status: member.status,
});

export type TeamRouteContentProps = {
  readonly members: readonly TeamMember[] | undefined;
  readonly error: unknown;
};

/** Thin glue over the query states; exported for direct testing. */
export function TeamRouteContent({ members, error }: TeamRouteContentProps) {
  if (error !== null && error !== undefined) {
    const denied = error instanceof ApiError && error.status === 403;
    return (
      <p role="alert" className="mx-6 my-4 text-md text-text-secondary">
        {denied
          ? 'Admin access is required to view the Team directory.'
          : 'Could not load the Team directory — please try again.'}
      </p>
    );
  }
  if (members === undefined) {
    return <p className="mx-6 my-4 text-md text-text-secondary">Loading the Team directory…</p>;
  }
  return <TeamPage members={members.map(toRow)} />;
}

function TeamRoute() {
  const navigate = useNavigate();
  const team = useTeamMembers();

  return (
    <AppShell
      sidebar={
        <Sidebar
          items={visibleNavItems()}
          activeId="team"
          searchPlaceholder="What are you looking for?"
          onNavigate={(id) => {
            const to = navTargetFor(id);
            if (to !== undefined && to !== '/team') void navigate({ to });
          }}
          onSignOut={() => signOut()}
        />
      }
      topbar={<TopBar label="TEAM" userName={currentUserName()} onSignOut={() => signOut()} />}
    >
      <TeamRouteContent members={team.data?.items} error={team.error} />
    </AppShell>
  );
}
